export const AGENDA_CALENDAR_VIEW_STORAGE_KEY = "agenda_calendar_view";
export const AGENDA_RESOURCE_PAGE_STORAGE_KEY = "agenda_resource_page";

export type AgendaCalendarView = "day" | "week";

export const AGENDA_RESOURCE_PAGE_SIZE = 6;

export const readAgendaCalendarView = (): AgendaCalendarView => {
  try {
    const stored = sessionStorage.getItem(AGENDA_CALENDAR_VIEW_STORAGE_KEY);

    return stored === "week" ? "week" : "day";
  } catch {
    return "day";
  }
};

export const writeAgendaCalendarView = (view: AgendaCalendarView): void => {
  try {
    sessionStorage.setItem(AGENDA_CALENDAR_VIEW_STORAGE_KEY, view);
  } catch {
    // sessionStorage unavailable — ignore
  }
};

export const readAgendaResourcePage = (): number => {
  try {
    const stored = sessionStorage.getItem(AGENDA_RESOURCE_PAGE_STORAGE_KEY);
    const parsed = Number(stored);

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

export const writeAgendaResourcePage = (page: number): void => {
  try {
    sessionStorage.setItem(AGENDA_RESOURCE_PAGE_STORAGE_KEY, String(Math.max(0, page)));
  } catch {
    // sessionStorage unavailable — ignore
  }
};

export const paginateIds = <T>(items: T[], page: number, pageSize: number): T[] => {
  if (items.length <= pageSize) {
    return items;
  }

  const start = page * pageSize;

  return items.slice(start, start + pageSize);
};

export const clampResourcePage = (page: number, totalItems: number, pageSize: number): number => {
  if (totalItems <= pageSize) {
    return 0;
  }

  const maxPage = Math.ceil(totalItems / pageSize) - 1;

  return Math.min(Math.max(0, page), maxPage);
};

export const buildWeekDayDates = (anchorDate: Date): Date[] => {
  const start = new Date(anchorDate);
  const day = start.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  start.setDate(start.getDate() + diffToMonday);
  start.setHours(12, 0, 0, 0);

  return Array.from({ length: 7 }, (_, index) => {
    const next = new Date(start);
    next.setDate(start.getDate() + index);

    return next;
  });
};

export const formatIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const parseAgendaDateParam = (value: string | null): string | null =>
  value && ISO_DATE_PATTERN.test(value) ? value : null;

export const parseResourcePageParam = (value: string | null): number | null => {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed >= 1 ? parsed - 1 : null;
};

export const buildAgendaSearchParams = (
  current: URLSearchParams,
  updates: {
    date?: string | null;
    resource_page?: number | null;
    client_id?: string | null;
    service_id?: string | null;
    unit_id?: string | null;
    action?: string | null;
  }
): URLSearchParams => {
  const next = new URLSearchParams(current);

  if (updates.date === null) {
    next.delete("date");
  } else if (updates.date) {
    next.set("date", updates.date);
  }

  if (updates.resource_page === null || updates.resource_page === 0) {
    next.delete("resource_page");
  } else if (updates.resource_page !== undefined && updates.resource_page > 0) {
    next.set("resource_page", String(updates.resource_page + 1));
  }

  if (updates.client_id === null) {
    next.delete("client_id");
  } else if (updates.client_id) {
    next.set("client_id", updates.client_id);
  }

  if (updates.service_id === null) {
    next.delete("service_id");
  } else if (updates.service_id) {
    next.set("service_id", updates.service_id);
  }

  if (updates.unit_id === null) {
    next.delete("unit_id");
  } else if (updates.unit_id) {
    next.set("unit_id", updates.unit_id);
  }

  if (updates.action === null) {
    next.delete("action");
  } else if (updates.action) {
    next.set("action", updates.action);
  }

  return next;
};
